import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { SidebarProvider, useSidebar } from '../../components/ui/Sidebar';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import AccountDetailModal from './components/AccountDetailModal';
import CreateAdminModal from './components/CreateAdminModal';
import { getFunctionErrorMessage } from '../../utils/functionError';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const StatusBadge = ({ value, trueLabel = 'Active', falseLabel = 'Inactive' }) => {
  const isActive = Boolean(value);
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${
      isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
    }`}>
      <Icon name={isActive ? 'CheckCircle2' : 'Circle'} size={12} />
      {isActive ? trueLabel : falseLabel}
    </span>
  );
};

const SuspendedBadge = () => (
  <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
    <Icon name="Ban" size={12} />
    Suspended
  </span>
);

const VerificationBadge = ({ status }) => {
  const config = {
    approved: { cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', icon: 'CheckCircle2' },
    pending: { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', icon: 'Clock' },
    rejected: { cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300', icon: 'XCircle' },
    unsubmitted: { cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400', icon: 'Circle' },
  };
  const c = config?.[status] || config?.unsubmitted;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${c?.cls}`}>
      <Icon name={c?.icon} size={12} />
      {status ? status?.charAt(0)?.toUpperCase() + status?.slice(1) : 'Unsubmitted'}
    </span>
  );
};

const RoleBadge = ({ role }) => {
  const colors = {
    super_admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    host_admin: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    host_manager: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
    host_staff: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md ${colors?.[role] || colors?.host_staff}`}>
      {role ? role?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase()) : '—'}
    </span>
  );
};

const EmptyState = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-16 gap-3">
    <Icon name="Users" size={48} className="text-muted-foreground" />
    <p className="text-muted-foreground text-sm">{message}</p>
  </div>
);

const LoadingState = () => (
  <div className="flex items-center justify-center py-16">
    <div className="flex items-center gap-3 text-muted-foreground">
      <Icon name="Loader2" size={24} className="animate-spin" />
      <span className="text-sm">Loading users...</span>
    </div>
  </div>
);

// ─── Admin Portal Users Table ────────────────────────────────────────────────
const AdminUsersTable = ({ users, loading, error, searchQuery, isSuperAdmin, onDelete }) => {
  const filtered = useMemo(() => {
    if (!searchQuery) return users;
    const q = searchQuery?.toLowerCase();
    return users?.filter(u =>
      u?.full_name?.toLowerCase()?.includes(q) ||
      u?.email?.toLowerCase()?.includes(q)
    );
  }, [users, searchQuery]);

  if (loading) return <LoadingState />;
  if (error) return <div className="py-8 text-center text-red-500 text-sm">{error}</div>;
  if (!filtered?.length) return <EmptyState message="No admin portal users found" />;

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border bg-muted/50">
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">User</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Phone</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Admin</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Joined</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Last Updated</th>
            {isSuperAdmin && <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {filtered?.map((user) => (
            <tr key={user?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                    {user?.avatar_url ? (
                      <img src={user?.avatar_url} alt={user?.full_name} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <Icon name="User" size={16} className="text-purple-600 dark:text-purple-400" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate flex items-center gap-1.5">
                      {user?.full_name || '—'}
                      {user?.is_super_admin && (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-primary/10 text-primary">
                          Super Admin
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">{user?.phone || '—'}</td>
              <td className="px-4 py-3">
                <StatusBadge value={user?.is_admin} trueLabel="Admin" falseLabel="User" />
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(user?.created_at)}</td>
              <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(user?.updated_at)}</td>
              {isSuperAdmin && (
                <td className="px-4 py-3">
                  {!user?.is_super_admin && (
                    <button
                      onClick={() => onDelete(user)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 dark:border-red-900/40 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <Icon name="Trash2" size={13} />
                      Delete
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Parent Users Table ───────────────────────────────────────────────────────
const ParentUsersTable = ({ users, loading, error, searchQuery, statusFilter, onViewDetails }) => {
  const filtered = useMemo(() => {
    let list = users;
    if (searchQuery) {
      const q = searchQuery?.toLowerCase();
      list = list?.filter(u =>
        u?.full_name?.toLowerCase()?.includes(q) ||
        u?.email?.toLowerCase()?.includes(q)
      );
    }
    if (statusFilter === 'active') list = list?.filter(u => !u?.is_suspended);
    if (statusFilter === 'suspended') list = list?.filter(u => u?.is_suspended);
    return list;
  }, [users, searchQuery, statusFilter]);

  if (loading) return <LoadingState />;
  if (error) return <div className="py-8 text-center text-red-500 text-sm">{error}</div>;
  if (!filtered?.length) return <EmptyState message="No parent users found" />;

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border bg-muted/50">
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">User</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Phone</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Joined</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered?.map((user) => (
            <tr key={user?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                    {user?.avatar_url ? (
                      <img src={user?.avatar_url} alt={user?.full_name} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <Icon name="User" size={16} className="text-blue-600 dark:text-blue-400" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{user?.full_name || '—'}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">{user?.phone || '—'}</td>
              <td className="px-4 py-3">
                {user?.is_suspended ? <SuspendedBadge /> : <StatusBadge value={true} trueLabel="Active" />}
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(user?.created_at)}</td>
              <td className="px-4 py-3">
                <button
                  onClick={() => onViewDetails(user, 'parent')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors text-foreground"
                >
                  <Icon name="Settings2" size={13} />
                  Manage
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Host Users Table ─────────────────────────────────────────────────────────
const HostUsersTable = ({ users, loading, error, searchQuery, statusFilter, onViewDetails }) => {
  const filtered = useMemo(() => {
    let list = users;
    if (searchQuery) {
      const q = searchQuery?.toLowerCase();
      list = list?.filter(u =>
        u?.full_name?.toLowerCase()?.includes(q) ||
        u?.email?.toLowerCase()?.includes(q) ||
        u?.organization_name?.toLowerCase()?.includes(q)
      );
    }
    if (statusFilter === 'active') list = list?.filter(u => !u?.is_suspended);
    if (statusFilter === 'suspended') list = list?.filter(u => u?.is_suspended);
    return list;
  }, [users, searchQuery, statusFilter]);

  if (loading) return <LoadingState />;
  if (error) return <div className="py-8 text-center text-red-500 text-sm">{error}</div>;
  if (!filtered?.length) return <EmptyState message="No host users found" />;

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border bg-muted/50">
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Host</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Organization</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Role</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Verification</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Rating</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered?.map((user) => (
            <tr key={user?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                    {user?.avatar_url ? (
                      <img src={user?.avatar_url} alt={user?.full_name} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <Icon name="Building2" size={16} className="text-green-600 dark:text-green-400" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{user?.full_name || '—'}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">{user?.organization_name || '—'}</td>
              <td className="px-4 py-3"><RoleBadge role={user?.role} /></td>
              <td className="px-4 py-3"><VerificationBadge status={user?.verification_status} /></td>
              <td className="px-4 py-3">
                {user?.is_suspended ? <SuspendedBadge /> : <StatusBadge value={true} trueLabel="Active" />}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <Icon name="Star" size={14} className="text-amber-500 fill-amber-500" />
                  <span className="text-sm text-foreground">{user?.avg_rating ? Number(user?.avg_rating)?.toFixed(1) : '—'}</span>
                  {user?.total_reviews > 0 && (
                    <span className="text-xs text-muted-foreground">({user?.total_reviews})</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <button
                  onClick={() => onViewDetails(user, 'host')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors text-foreground"
                >
                  <Icon name="Settings2" size={13} />
                  Manage
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Stats Cards ──────────────────────────────────────────────────────────────
const StatsCards = ({ adminCount, parentCount, hostCount, suspendedCount }) => {
  const cards = [
    { title: 'Admin Portal Users', value: adminCount, icon: 'ShieldCheck', color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-100 dark:bg-purple-900/30' },
    { title: 'Parent Users', value: parentCount, icon: 'Users', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30' },
    { title: 'Host Users', value: hostCount, icon: 'Building2', color: 'text-green-600 dark:text-green-400', bg: 'bg-green-100 dark:bg-green-900/30' },
    { title: 'Suspended Accounts', value: suspendedCount, icon: 'Ban', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-900/30' },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards?.map((card, i) => (
        <div key={i} className="card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">{card?.title}</p>
              <p className="text-2xl font-bold text-foreground">{card?.value}</p>
            </div>
            <div className={`w-12 h-12 rounded-lg ${card?.bg} flex items-center justify-center`}>
              <Icon name={card?.icon} size={24} className={card?.color} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const TABS = [
  { id: 'admin', label: 'Admin Portal Users', icon: 'ShieldCheck' },
  { id: 'parents', label: 'Parent Users', icon: 'Users' },
  { id: 'hosts', label: 'Host Users', icon: 'Building2' },
];

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
];

const UserManagementContent = () => {
  const { isCollapsed } = useSidebar();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.email === 'admin@kidquest.com';
  const [activeTab, setActiveTab] = useState('admin');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [adminUsers, setAdminUsers] = useState([]);
  const [parentUsers, setParentUsers] = useState([]);
  const [hostUsers, setHostUsers] = useState([]);

  const [loadingAdmin, setLoadingAdmin] = useState(true);
  const [loadingParents, setLoadingParents] = useState(true);
  const [loadingHosts, setLoadingHosts] = useState(true);

  const [errorAdmin, setErrorAdmin] = useState(null);
  const [errorParents, setErrorParents] = useState(null);
  const [errorHosts, setErrorHosts] = useState(null);

  // Account detail modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserType, setSelectedUserType] = useState(null);

  // Create/delete admin
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoadingAdmin(true);
    setLoadingParents(true);
    setLoadingHosts(true);
    setErrorAdmin(null);
    setErrorParents(null);
    setErrorHosts(null);

    const [adminRes, parentRes, hostRes] = await Promise.all([
      supabase?.from('parent_profiles')?.select('id, email, full_name, phone, avatar_url, is_admin, is_super_admin, is_suspended, suspended_at, suspended_reason, created_at, updated_at')?.eq('is_admin', true)?.order('created_at', { ascending: false }),
      supabase?.from('parent_profiles')?.select('id, email, full_name, phone, avatar_url, is_admin, is_suspended, suspended_at, suspended_reason, created_at, updated_at')?.neq('is_admin', true)?.order('created_at', { ascending: false }),
      supabase?.from('event_host_profiles')?.select('id, email, full_name, organization_name, phone, avatar_url, role, verification_status, is_verified, avg_rating, total_reviews, host_type, bio, is_suspended, suspended_at, suspended_reason, created_at, updated_at')?.order('created_at', { ascending: false }),
    ]);

    if (adminRes?.error) setErrorAdmin(adminRes?.error?.message);
    else setAdminUsers(adminRes?.data || []);
    setLoadingAdmin(false);

    if (parentRes?.error) setErrorParents(parentRes?.error?.message);
    else setParentUsers(parentRes?.data || []);
    setLoadingParents(false);

    if (hostRes?.error) setErrorHosts(hostRes?.error?.message);
    else setHostUsers(hostRes?.data || []);
    setLoadingHosts(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleViewDetails = (user, type) => {
    setSelectedUser(user);
    setSelectedUserType(type);
  };

  const handleModalClose = () => {
    setSelectedUser(null);
    setSelectedUserType(null);
  };

  const handleRefreshAfterAction = useCallback(() => {
    fetchAll()?.then(() => {
      // After refresh, update the selected user from fresh data if modal is open
    });
    fetchAll();
  }, [fetchAll]);

  // When data refreshes, sync selected user
  useEffect(() => {
    if (!selectedUser) return;
    if (selectedUserType === 'parent') {
      const updated = parentUsers?.find(u => u?.id === selectedUser?.id);
      if (updated) setSelectedUser(updated);
    } else if (selectedUserType === 'host') {
      const updated = hostUsers?.find(u => u?.id === selectedUser?.id);
      if (updated) setSelectedUser(updated);
    }
  }, [parentUsers, hostUsers]);

  const suspendedCount = useMemo(() => {
    return [...parentUsers, ...hostUsers]?.filter(u => u?.is_suspended)?.length;
  }, [parentUsers, hostUsers]);

  const handleSuspend = useCallback(async (userId, reason) => {
    await supabase?.from(selectedUserType === 'host' ? 'event_host_profiles' : 'parent_profiles')
      ?.update({ is_suspended: true, suspended_at: new Date().toISOString(), suspended_reason: reason })
      ?.eq('id', userId);
    fetchAll();
  }, [selectedUserType, fetchAll]);

  const handleReactivate = useCallback(async (userId) => {
    await supabase?.from(selectedUserType === 'host' ? 'event_host_profiles' : 'parent_profiles')
      ?.update({ is_suspended: false, suspended_at: null, suspended_reason: null })
      ?.eq('id', userId);
    fetchAll();
  }, [selectedUserType, fetchAll]);

  const handlePasswordReset = useCallback(async (email) => {
    await supabase?.auth?.resetPasswordForEmail(email);
  }, []);

  const handleEscalate = useCallback(async (userId, note) => {
    // Escalation logic placeholder
    console.log('Escalate user', userId, note);
  }, []);

  const handleDeleteAdmin = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const { data: sessionData } = await supabase?.auth?.getSession();
      const { data, error } = await supabase?.functions?.invoke('delete-admin-user', {
        body: { userId: deleteTarget?.id },
        headers: { Authorization: `Bearer ${sessionData?.session?.access_token}` },
      });
      if (error) throw new Error(await getFunctionErrorMessage(error, 'Failed to delete admin'));
      if (data?.error) throw new Error(data.error);
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete admin');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, fetchAll]);

  return (
    <>
      <Sidebar />
      <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <Breadcrumbs items={[
              { label: 'User Management', href: '/users' }
            ]} />
            <h1 className="text-3xl font-bold text-foreground mt-2">User Management</h1>
            <p className="text-muted-foreground mt-1">Manage admin portal users, parents, and event hosts</p>
          </div>
          <div className="flex items-center gap-3">
            {isSuperAdmin && activeTab === 'admin' && (
              <button
                onClick={() => setShowCreateAdmin(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary/5 transition-colors"
              >
                <Icon name="UserPlus" size={14} />
                Create Admin
              </button>
            )}
            <button
              onClick={fetchAll}
              className="group flex items-center gap-2 px-3 py-2 text-sm font-medium bg-primary text-primary-foreground border border-primary rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Icon name="RefreshCw" size={14} className="transition-transform duration-500 group-hover:rotate-[360deg]" />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <StatsCards
          adminCount={adminUsers?.length}
          parentCount={parentUsers?.length}
          hostCount={hostUsers?.length}
          suspendedCount={suspendedCount}
        />

        {/* Search + Status Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative max-w-sm flex-1">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={14} />
              </button>
            )}
          </div>
          {(activeTab === 'parents' || activeTab === 'hosts') && (
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              {STATUS_FILTERS?.map(f => (
                <button
                  key={f?.value}
                  onClick={() => setStatusFilter(f?.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    statusFilter === f?.value
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {f?.label}
                  {f?.value === 'suspended' && suspendedCount > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 rounded-full">
                      {suspendedCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="card mb-6">
          <div className="border-b border-border">
            <div className="flex gap-0">
              {TABS?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => { setActiveTab(tab?.id); setStatusFilter('all'); }}
                  className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors duration-200 ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  {tab?.label}
                  <span className={`ml-1 px-2 py-0.5 text-xs rounded-full ${
                    activeTab === tab?.id ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                  }`}>
                    {tab?.id === 'admin' ? adminUsers?.length
                      : tab?.id === 'parents' ? parentUsers?.length
                      : hostUsers?.length}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-0">
            {activeTab === 'admin' && (
              <AdminUsersTable
                users={adminUsers}
                loading={loadingAdmin}
                error={errorAdmin}
                searchQuery={searchQuery}
                isSuperAdmin={isSuperAdmin}
                onDelete={(user) => { setDeleteTarget(user); setDeleteError(null); }}
              />
            )}
            {activeTab === 'parents' && (
              <ParentUsersTable
                users={parentUsers}
                loading={loadingParents}
                error={errorParents}
                searchQuery={searchQuery}
                statusFilter={statusFilter}
                onViewDetails={handleViewDetails}
              />
            )}
            {activeTab === 'hosts' && (
              <HostUsersTable
                users={hostUsers}
                loading={loadingHosts}
                error={errorHosts}
                searchQuery={searchQuery}
                statusFilter={statusFilter}
                onViewDetails={handleViewDetails}
              />
            )}
          </div>
        </div>
      </div>
      {/* Account Detail Modal */}
      {selectedUser && (
        <AccountDetailModal
          user={selectedUser}
          userType={selectedUserType}
          onClose={handleModalClose}
          onRefresh={handleRefreshAfterAction}
          onSuspend={handleSuspend}
          onReactivate={handleReactivate}
          onPasswordReset={handlePasswordReset}
          onEscalate={handleEscalate}
        />
      )}

      {/* Create Admin Modal */}
      {showCreateAdmin && (
        <CreateAdminModal
          onClose={() => setShowCreateAdmin(false)}
          onCreated={() => { setShowCreateAdmin(false); fetchAll(); }}
        />
      )}

      {/* Delete Admin Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">Delete Admin Account</h3>
              <button
                onClick={() => { setDeleteTarget(null); setDeleteError(null); }}
                className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                You are about to permanently delete{' '}
                <span className="font-medium text-foreground">"{deleteTarget?.full_name || deleteTarget?.email}"</span>{' '}
                as an admin portal user. This cannot be undone.
              </p>
              {deleteError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  <Icon name="AlertCircle" size={14} />
                  {deleteError}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={() => { setDeleteTarget(null); setDeleteError(null); }}
                  className="flex-1 px-4 py-2.5 text-sm font-medium border border-border rounded-xl hover:bg-muted transition-colors text-foreground"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAdmin}
                  disabled={deleteLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleteLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icon name="Trash2" size={14} />
                  )}
                  Delete Admin
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const UserManagement = () => {
  return (
    <SidebarProvider>
      <UserManagementContent />
    </SidebarProvider>
  );
};

export default UserManagement;